// MIDAS V3 — Machine pill (Phase 2)
//
// Ports `MachinePillW` from the design handoff:
//   ToDo/MidasChat/Claude_Design_files/machineid/design_handoff_midas_v3/
//   chat-window.jsx (lines 55-74)
//
// Two variants:
//   • Machine variant (default): green pip + machine name (Sora 500/12) +
//     `SN xxxxx` (mono 11, faint)
//   • Platform variant (`platform` prop): accent `◆ PLATFORM` (mono 10,
//     letter-spaced) + "Machine ID" body (Sora 500)
//
// Reads `state.chatBot.machineContext` to derive the name and serial. When no
// machineContext is set, falls back to a dashed-outline "Pick a machine" pill.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React from "react";

import { useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";

export interface MachinePillV3Props {
  /** When true, renders the platform-mode variant (◆ PLATFORM · Machine ID). */
  platform?: boolean;
  /** Tightens padding for use inside the composer flex row. */
  compact?: boolean;
  /** MODEL-PICKER P3 (attach columns): render THIS machine instead of the
   *  global chatBot.machineContext — attached columns have their OWN machine
   *  scope and must not display the primary chat's context. When set, the
   *  machine variant renders with the given name (serial optional). */
  machineOverride?: { name: string; serialNumber?: string };
}

const MachinePillV3: React.FC<MachinePillV3Props> = ({
  platform = false,
  compact = false,
  machineOverride,
}) => {
  const { translate } = useTranslation("chatBot");
  const machineContext = useAppSelector((s) => s.chatBot.machineContext);

  const paddingY = compact ? 3 : 4;
  const paddingX = 10;

  if (platform) {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: `${paddingY}px ${paddingX}px`,
          background: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: 999,
          fontSize: 12,
          color: "var(--ink-2)",
          whiteSpace: "nowrap",
        }}
      >
        <span
          aria-hidden="true"
          className="mono"
          style={{
            fontSize: 10,
            letterSpacing: "0.1em",
            color: "var(--accent)",
            fontWeight: 700,
          }}
        >
          ◆ PLATFORM
        </span>
        <span style={{ fontWeight: 500 }}>Machine ID</span>
      </span>
    );
  }

  // No machine context → muted "pick a machine" pill.
  if (!machineOverride && !machineContext) {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: `${paddingY}px ${paddingX}px`,
          background: "var(--card)",
          border: "1px dashed var(--border)",
          borderRadius: 999,
          fontSize: 12,
          color: "var(--muted)",
          whiteSpace: "nowrap",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            background: "var(--faint)",
          }}
        />
        {translate("midas.v3.pickMachine", {
          defaultValue: "Pick a machine",
        }) as string}
      </span>
    );
  }

  // Resolve a stable display name. Mirrors v2's logic:
  //   prefer "manufacturer model", fall back to model, then internalNumber,
  //   then "SN xxxxx", then a generic "Active machine" string.
  // Column override wins over the global context (attach columns).
  const model = machineOverride ? "" : machineContext?.model?.trim() || "";
  const brand = machineOverride
    ? ""
    : machineContext?.manufacturerName?.trim() || "";
  const name = machineOverride
    ? machineOverride.name
    : model
    ? brand
      ? `${brand} ${model}`
      : model
    : "";
  const serial = machineOverride
    ? machineOverride.serialNumber?.trim() || ""
    : machineContext?.serialNumber?.trim() || "";

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: `${paddingY}px ${paddingX}px`,
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderRadius: 999,
        fontSize: 12,
        color: "var(--ink-2)",
        whiteSpace: "nowrap",
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          background: "var(--green)",
          flexShrink: 0,
        }}
      />
      {name && (
        <span
          style={{
            fontWeight: 500,
            color: "var(--ink-2)",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {name}
        </span>
      )}
      {serial && (
        <span
          className="mono"
          style={{
            fontSize: 11,
            color: "var(--faint)",
            flexShrink: 0,
          }}
        >
          SN {serial}
        </span>
      )}
    </span>
  );
};

export default MachinePillV3;
